/* Checkout-intercept offer modal.
   Markup: snippets/hl-offer-modal.liquid. Config + eligible ids: window.hlOffer (hl-home.js).

   Pattern from sadboysaga.com's sbs-freeshoe.js (studied live 2026-07-28): a CAPTURE-phase
   document listener on every checkout control swallows the click while the free-gift offer is
   still incomplete, shows the shopper what they're leaving behind, and always offers a
   one-click way through.

   FAIL-OPEN IS THE CONTRACT. Every unknown — no config, no markup, ids not loaded, cart state
   unresolvable — must let the click through to /checkout. A bug in here should cost a nudge,
   never an order. Nothing in this file may ever be the reason a shopper can't check out.

   Counting follows the paid-gate split documented in snippets/hl-cart-drawer.liquid: the
   shopper is only ever asked for the PAID units (max 3); the free ones are framed as slots to
   claim, with the pip counter as the honest signal that they aren't claimed yet. The Shopify
   discount itself is untouched — still all-or-nothing at `goal` (8) eligible units. */
(function () {
  'use strict';
  var modal = document.querySelector('[data-hl-offer-modal]');
  if (!modal) return;
  var overlay = document.querySelector('[data-hl-offer-overlay]');
  var cfg = (window.hlOffer && window.hlOffer.cfg) || null;
  /* v2 config only. A stale cached copy of this file against new Liquid, or new JS against a
     cached v1 blob, both land here and stand down rather than guessing. The rollout flag itself
     is enforced inside the model (rungsFor('off') is empty), not duplicated here. */
  if (!cfg || cfg.v !== 2 || !window.hlOfferModel) return;

  /* The offer model, resolved ONCE at module scope. The guard above already refused to run
     without it, so this cannot be undefined here. It used to be re-read as a local inside
     stateNow() only, which meant every other function referencing `M` was a ReferenceError
     waiting for its first call — upsellView() shipped exactly that bug and the checkout
     fail-open path is what caught it. */
  var M = window.hlOfferModel;

  var cart = null;                 // freshest cart we know about
  var lastFocus = null;

  /* ---- PROMPT BUDGET ------------------------------------------------------------------------
     One prompt per page view. Severity ordering exists so a LESS urgent prompt can never be
     shown after a more urgent one (a downgrade), which is a bug in every configuration.

     Whether the reverse — one ESCALATION from upsell to the blocking intercept — is allowed was
     put to the operator on 2026-08-19 with the concrete path in front of him: she dismisses the
     upsell at 8 units, removes a discount piece, drops to `none` having lost all 5 free pieces,
     and clicks checkout. He ruled Option A: one prompt, period. The drawer still visibly regresses
     to 4/5 in front of her, so the loss is not silent — but nothing stops the checkout.
     Flip this one constant to re-open that decision; nothing else needs to change. */
  var ALLOW_PROMPT_ESCALATION = false;
  var SEVERITY = { upsell: 1, intercept: 2 };
  var promptedLevel = 0;
  function mayPrompt(mode) {
    var lvl = SEVERITY[mode] || 0;
    if (!lvl) return false;
    if (promptedLevel === 0) return true;
    return ALLOW_PROMPT_ESCALATION && lvl > promptedLevel;
  }
  function spendPrompt(mode) {
    var lvl = SEVERITY[mode] || 0;
    if (lvl > promptedLevel) promptedLevel = lvl;
  }

  function $(s) { return modal.querySelector(s); }
  function $$(s) { return Array.prototype.slice.call(modal.querySelectorAll(s)); }
  function money(c) { return '$' + (Number(c || 0) / 100).toFixed(2).replace(/\.00$/, ''); }
  function plural(n, one, many) { return n === 1 ? one : many; }

  /* THE STATE. All of it now comes from window.hlOfferModel (PLAN §3) — this file used to carry
     its own copy of the tier arithmetic, which is how B-017 happened: `shortBy` was derived from
     the distance to the NEXT goal, so at exactly 8 units it read 8 rather than 0 and the
     intercept fired on a shopper who owed nothing.

     `cart` is the freshest /cart.js we have; until it arrives we fall back to the SERVER's
     page-load snapshot in cfg, which carries qty, dcqty AND the allocation state. That snapshot
     is what lets the very first checkout click be judged correctly — previously `cart` was null
     there, so a returning shopper with a stocked cart got no prompt at all. */
  function stateNow() {
    if (!M || !cfg) return null;
    var o = window.hlOffer || {};

    var units = null, picks = null, alloc = null;
    if (cart && o.ids) {
      units = 0;
      cart.items.forEach(function (it) { if (o.ids.indexOf(it.product_id) > -1) units += it.quantity; });
      if (o.pickIds) {
        picks = 0;
        cart.items.forEach(function (it) { if (o.pickIds.indexOf(it.product_id) > -1) picks += it.quantity; });
      }
    }
    /* The server snapshot is exact, but only while the cart is untouched. After a client-side
       mutation with no ids loaded we genuinely do not know — and the contract is to fail open,
       never to guess. */
    if (units === null) {
      if (o.mutated || typeof cfg.qty !== 'number') return null;
      units = cfg.qty;
    }
    if (picks === null) {
      if (o.mutated || typeof cfg.dcqty !== 'number') return null;
      picks = cfg.dcqty;
    }
    /* Prefer the live cart's allocations; fall back to the server's rendered snapshot. */
    if (!cart) {
      alloc = {
        state: cfg.allocState || 'unknown',
        rung: null, serverFree: cfg.serverFree || 0, title: cfg.appliedTitle || null
      };
      M.rungsFor(cfg.rollout).forEach(function (r) { if (r.title === alloc.title) alloc.rung = r; });
      if (alloc.state === 'exact' && !alloc.rung) alloc.state = 'unknown';
    }

    return M.derive({
      units: units, picks: picks, rollout: cfg.rollout,
      cart: alloc ? null : cart, alloc: alloc
    });
  }

  function copy(st) {
    /* Keyed off what the SERVER granted, never off eligibility. A cart of `goal` eligible rings
       holding none of the pieces the discount can zero used to reach the "all claimed" branch
       while checkout charged full price. */
    if (st.confirmed && !st.next) return {
      title: 'All ' + st.freeNow + ' free pieces — claimed.',
      sub: 'They come off automatically at checkout. You’re only paying for ' + st.paysFor + '.'
    };
    /* Top rung reached on paper but nothing granted — an anomaly, so say something true and
       actionable with no numbers attached to it. */
    if (!st.next) return {
      title: 'Pick your free pieces.',
      sub: 'They come from the discount collection — add them and they come off at checkout.'
    };
    var free = st.next.free, paid = st.next.paid;
    /* "then pick N free", never "unlocks N free": the discount applies at next.goal, not at
       next.paid, so nothing here promises money off before the rung completes. */
    if (st.paidLeft > 0) return {
      title: 'Add ' + st.paidLeft + ' more, then pick ' + free + ' free.',
      sub: 'Buy ' + paid + ', get ' + free + ' free. ' + st.paidLeft + ' more ' +
           plural(st.paidLeft, 'piece qualifies', 'pieces qualify') + ' you — then choose ' + free +
           ' on us, free at checkout.'
    };
    if (st.pipFilled === 0) return {
      title: 'You’re in — now pick your ' + free + ' free.',
      sub: 'The next ' + free + ' are on us. Add all ' + free +
           ' and they come off automatically at checkout.'
    };
    if (st.slotsNeed === 1) return {
      title: '1 more free piece to grab.',
      sub: 'That last one is what flips ' + free + ' of them to free. Don’t leave it behind.'
    };
    return {
      title: st.slotsNeed + ' more free pieces to grab.',
      sub: 'Fill all ' + free + ' free slots and they come off at checkout.'
    };
  }

  function pips(st) {
    var h = '';
    /* pipFilled, not filled — a slot lights only when backed by a piece the discount can zero. */
    for (var i = 0; i < st.pipTotal; i++) h += '<span class="cd__pip' + (i < st.pipFilled ? ' on' : '') + '"></span>';
    return '<div class="cd__pips" role="img" aria-label="' + st.pipFilled + ' of ' + st.pipTotal +
           ' free pieces in your cart">' + h +
           '<span class="cd__pipcount"><b>' + st.pipFilled + '</b>/' + st.pipTotal + ' free</span></div>';
  }

  /* UPSELL MODE. She already HAS a rung; another is still reachable. Two steps, taken straight
     from the model's rows so no new state is invented:
       row 3a -> short on PAID units      -> Belly Bars (operator's destination, 2026-08-19)
       row 3b -> short on FREE picks      -> Discount Collection (all-qualifying by construction)
     The product grid is deliberately NOT reused here. It renders discount-collection, which is
     the wrong shelf for the paid step, and the operator's 2026-08-18 ruling is that these CTAs
     navigate rather than open a picker. */
  function upsellView(st) {
    var U = M.UPSELL_COPY;
    /* Which step she is on comes from the MODEL, not from re-testing paidLeft here — that is what
       let the drawer and this modal reach opposite conclusions about the same cart. */
    var paidStep = st.ctaKind === 'paid';
    var n = paidStep ? st.paidLeft : st.slotsNeed;
    /* pieceWord travels WITH the count it agrees with. paidLeft is 1-2 and slotsNeed is 1-3, so
       "1 free pieces" is reachable and is a defect, not a typo. */
    var vars = { paidLeft: st.paidLeft, slotsNeed: st.slotsNeed, freeLeft: st.freeLeft,
                 pieceWord: M.pieceWord(n) };
    return {
      title: M.fill(paidStep ? U.paidTitle : U.picksTitle, vars),
      sub:   M.fill(paidStep ? U.paidSub   : U.picksSub,   vars),
      cta:   st.ctaLabel,
      href:  st.ctaHref
    };
  }

  function renderUpsell(st) {
    /* She stopped qualifying while the modal was open (removed an item, or the flag went dark).
       Do not repaint an upsell for a rung she can no longer reach — close and let her go. */
    if (!st.mayUpsell) { close('upsell_state_lost'); return; }
    var v = upsellView(st);
    $('[data-ofm-title]').textContent = v.title;
    $('[data-ofm-sub]').textContent = v.sub;
    $('[data-ofm-prog]').innerHTML = pips(st);   // gateMet is always true when a rung is confirmed
    var link = $('[data-ofm-upsell-cta]');
    if (link) { link.textContent = v.cta; link.setAttribute('href', v.href); }
    var eyebrow = $('[data-ofm-eyebrow]');
    if (eyebrow && st.next) eyebrow.textContent = 'Buy ' + st.next.paid + ', Get ' + st.next.free + ' Free';
    var items = (cart && cart.items) || [];
    $('[data-ofm-thumbs]').innerHTML = items.slice(0, 6).map(function (it) {
      return it.image ? '<img src="' + it.image + '" alt="" width="34" height="34" loading="lazy">' : '';
    }).join('');
    $('[data-ofm-count]').textContent = cart
      ? cart.item_count + ' ' + plural(cart.item_count, 'item', 'items') : '';
    $('[data-ofm-subtotal]').textContent = cart ? money(cart.total_price) : '';
  }

  function render() {
    var st = stateNow();
    /* Unresolvable state: leave the server-rendered markup exactly as it is rather than
       repainting it from a guess. */
    if (!st) return;
    if (modal.classList.contains('is-upsell')) return renderUpsell(st);
    var c = copy(st);
    $('[data-ofm-title]').textContent = c.title;
    $('[data-ofm-sub]').textContent = c.sub;
    /* Bar while the paid gate is open, pips once it's met — mirrors the drawer exactly, which is
       why it reads st.gateMet rather than re-deriving. The old `st.paidLeft === 0` test agreed
       with the drawer only while a confirmed rung pinned the track full; with the track now
       widening to 5/8 it would draw a bar where the drawer draws dots. */
    var pct = st.next && st.next.paid
      ? Math.min(100, Math.round(st.paysFor / st.next.paid * 100)) : 0;
    $('[data-ofm-prog]').innerHTML = st.gateMet
      ? pips(st)
      : '<div class="cd__bar"><span style="width:' + pct + '%"></span></div>';
    modal.classList.toggle('is-maxed', !!st.confirmed && !st.next);

    var items = (cart && cart.items) || [];
    $('[data-ofm-thumbs]').innerHTML = items.slice(0, 6).map(function (it) {
      return it.image ? '<img src="' + it.image + '" alt="" width="34" height="34" loading="lazy">' : '';
    }).join('');
    $('[data-ofm-count]').textContent = cart
      ? cart.item_count + ' ' + plural(cart.item_count, 'item', 'items') : '';
    $('[data-ofm-subtotal]').textContent = cart ? money(cart.total_price) : '';

    /* Hide cards for products already in the cart, and re-show them if they get removed. Any open
       option sheet is dismissed here too: a successful add re-renders, and leaving the sheet up
       would show variants for a product that just left the grid. */
    closeSheet();
    var have = {};
    items.forEach(function (it) { have[it.product_id] = 1; });
    $$('[data-ofm-card]').forEach(function (card) {
      have[card.getAttribute('data-pid')] ? card.setAttribute('hidden', '') : card.removeAttribute('hidden');
      var add = card.querySelector('[data-ofm-add]');
      if (add) {
        add.disabled = false;
        add.textContent = add.hasAttribute('data-ofm-needs-options') ? 'Options' : 'Add';
      }
    });
  }

  /* ---- Same-size shortcut -------------------------------------------------------------------
     The shopper already picked a size on the PDP; making them re-read a list of
     "G23 Titanium Bar / 10MM Bar (STANDARD SIZE) / 14G (STANDARD SIZE)" rows to pick it again is
     the friction this removes.

     BAR LENGTH AND GAUGE ARE DIFFERENT PHYSICAL DIMENSIONS. A 10MM/16G bar does not fit where a
     10MM/14G does, so they are never aliased and a bare "same size" is never claimed. Matching is
     on NAMED options only — the joined variant title is ambiguous by construction and is never
     regex-parsed.

     Everything here fails CLOSED to the full list. A wrong one-click ships the wrong jewellery;
     an absent shortcut costs nothing. */
  var FIT_DIMS = {
    bar_length: ['size', 'bar size', 'bar length'],
    gauge: ['gauge', 'gauge size'],
    material: ['material', 'bar material']
  };
  /* Dimensions that must actually FIT. `material` is a preference, not a fit constraint, so it is
     deliberately not required to match — but it is never named in the label either. */
  var REQUIRED_FIT = ['bar_length', 'gauge'];

  function dimOf(optName) {
    var n = String(optName == null ? '' : optName).trim().toLowerCase();
    for (var d in FIT_DIMS) {
      if (Object.prototype.hasOwnProperty.call(FIT_DIMS, d) && FIT_DIMS[d].indexOf(n) > -1) return d;
    }
    return null;
  }
  /* "10MM Bar (STANDARD SIZE)", "10MM Bar (Standard Size)" and "10MM Bar" are the same size spelled
     three ways in this catalogue. Strip the parenthetical, collapse space, uppercase. */
  function normVal(v) {
    return String(v == null ? '' : v).toUpperCase().replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
  }
  /* Material is compared at FAMILY level because the same material is written four ways across
     this catalogue — "316L Surgical Steel Bar", "316L Surgical Steel Belly Bar",
     "316L Surgical Steel", "G23 Titanium Bar", "G23 Titanium Belly Bar". Exact-string comparison
     would refuse almost every real pair. */
  function matFamily(v) {
    var s = normVal(v);
    if (s.indexOf('TITANIUM') > -1) return 'TITANIUM';
    if (s.indexOf('STEEL') > -1) return 'STEEL';
    if (s.indexOf('GOLD') > -1) return 'GOLD';
    return s || null;
  }
  function tupleFrom(pairs) {
    var t = {};
    for (var i = 0; i < pairs.length; i++) {
      var d = dimOf(pairs[i].name);
      if (!d) continue;
      t[d] = (d === 'material') ? matFamily(pairs[i].value) : normVal(pairs[i].value);
    }
    return t;
  }
  function tupleKey(t) {
    var ks = [], k;
    for (k in t) if (Object.prototype.hasOwnProperty.call(t, k)) ks.push(k);
    ks.sort();
    return ks.map(function (x) { return x + '=' + t[x]; }).join('|');
  }

  /* One tuple PER CART LINE, never unioned across lines: a 10MM/16G line plus a bare 10MM line
     must not combine into a "10MM/16G" intent nobody expressed. Lines must agree exactly. */
  function soleCartTuple() {
    var items = (cart && cart.items) || [], tuples = [], i;
    for (i = 0; i < items.length; i++) {
      var owv = items[i].options_with_values;
      if (!owv || !owv.length) continue;
      var t = tupleFrom(owv);
      if (tupleKey(t)) tuples.push(t);
    }
    if (!tuples.length) return null;
    var key = tupleKey(tuples[0]);
    for (i = 1; i < tuples.length; i++) if (tupleKey(tuples[i]) !== key) return null;
    return tuples[0];
  }

  function variantTuple(names, v) {
    var pairs = [], i;
    for (i = 0; i < names.length; i++) pairs.push({ name: names[i], value: (v.o || [])[i] });
    return tupleFrom(pairs);
  }

  /* Exactly one AVAILABLE variant whose required fit dimensions all match, or null.
     A dimension present on one side but not the other is a MISMATCH, never a pass — absence is
     not evidence that a gauge is inapplicable. */
  function findSameFit(names, vs, want) {
    if (!want || want.bar_length == null) return null;   // nothing to anchor on
    var hits = [], i, j;
    for (i = 0; i < vs.length; i++) {
      if (!vs[i].a) continue;
      var got = variantTuple(names, vs[i]), ok = true;
      for (j = 0; j < REQUIRED_FIT.length; j++) {
        var d = REQUIRED_FIT[j];
        var a = want[d], b = got[d];
        if (a == null && b == null) continue;            // dimension absent on BOTH: nothing to get wrong
        if (a == null || b == null || a !== b) { ok = false; break; }
      }
      if (ok) hits.push({ v: vs[i], t: got });
    }
    /* Material NARROWS an ambiguous fit match; it never creates one and never disqualifies.
       The catalogue's most common multi-variant shape is Material x Size (17 of 25 sized
       products), where a bar length alone legitimately matches two variants — without this the
       shortcut could only ever fire on the other 8. Both sides must expose material for it to
       apply, and a failed narrowing leaves the ambiguity intact, so the result is still "no
       shortcut" rather than a guess. */
    if (hits.length > 1 && want.material) {
      var narrowed = [];
      for (i = 0; i < hits.length; i++) {
        if (hits[i].t.material && hits[i].t.material === want.material) narrowed.push(hits[i]);
      }
      if (narrowed.length) hits = narrowed;
    }
    return hits.length === 1 ? hits[0] : null;
  }

  /* Name only what was actually matched. Never "same size" — that would imply parity on dimensions
     we did not verify. */
  function fitLabel(t) {
    var parts = [];
    if (t.bar_length) parts.push(t.bar_length.replace(/\s*BAR\s*$/, '') + ' bar');
    if (t.gauge) parts.push(t.gauge);
    return parts.length ? 'Same ' + parts.join(' · ') : null;
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function optRow(v) {
    return '<button type="button" class="ofm__opt' + (v.a ? '' : ' is-oos') + '" data-vid="' + v.id + '"' +
           (v.a ? '' : ' disabled') + '><span>' + esc(v.t) + '</span><i>' +
           (v.a ? money(v.p) : 'Sold out') + '</i></button>';
  }

  /* Option sheet — one row per variant, over the grid, full modal width. Escapes the card's
     third-of-a-row so three-axis variant titles stay readable. */
  function openSheet(card) {
    var sheet = $('[data-ofm-sheet]'), box = $('[data-ofm-opts]'), name = $('[data-ofm-sheetname]');
    var src = card && card.querySelector('[data-ofm-variants]');
    if (!sheet || !box || !src) return;

    var blob = null;
    try { blob = JSON.parse(src.textContent); } catch (err) { blob = null; }
    /* Tolerate the pre-2026-08 shape (a bare array, no option data) so a stale cached asset
       degrades to the plain list instead of breaking the sheet. */
    var vs = [], names = [];
    if (blob && Object.prototype.toString.call(blob) === '[object Array]') vs = blob;
    else if (blob && blob.v) { vs = blob.v || []; names = blob.n || []; }
    if (!vs.length) return;

    name.textContent = card.querySelector('.ofm__name').textContent;

    var same = null, label = null;
    try {
      if (names.length) {
        var hit = findSameFit(names, vs, soleCartTuple());
        if (hit) { label = fitLabel(hit.t); if (label) same = hit.v; }
      }
    } catch (err) { same = null; }   // fail closed — full list

    /* G4 (PLAN §7): EXPOSURE, not just use. A shortcut that is never OFFERED is a completely
       different failure from one shoppers are offered and ignore, and only the offered-vs-taken
       ratio tells the two apart. `named_options: 0` is the specific "stale cached asset served
       the pre-2026-08 bare-array shape" case, which also produces no shortcut. */
    if (window.hlTrack) {
      window.hlTrack(same ? 'hl_samesize_exposed' : 'hl_samesize_absent', {
        product_id: card.getAttribute('data-pid'),
        variant_count: vs.length,
        named_options: names.length,
        label: label || null
      });
    }

    var rows = vs.map(optRow).join('');
    if (same) {
      box.innerHTML =
        '<button type="button" class="ofm__opt ofm__opt--same" data-vid="' + same.id + '">' +
          '<span>' + esc(label) + '</span><i>' + money(same.p) + '</i>' +
        '</button>' +
        '<button type="button" class="ofm__more" data-ofm-more>Pick a different size</button>' +
        '<div class="ofm__optlist" data-ofm-optlist hidden>' + rows + '</div>';
    } else {
      box.innerHTML = rows;
    }

    sheet.hidden = false;
    sheet.scrollTop = 0;
    var first = box.querySelector('.ofm__opt:not([disabled])');
    if (first) first.focus();
  }
  function closeSheet() {
    var sheet = $('[data-ofm-sheet]');
    if (sheet) sheet.hidden = true;
  }

  function open(via, opts) {
    opts = opts || {};
    /* A cancellation token from the checkout attempt that swallowed the click. If its fail-open
       timeout already fired, that shopper is mid-navigation to checkout and this modal must NOT
       paint over the top of it. Checked inside every animation frame, because that is precisely
       where the delay that caused the timeout would be. */
    var token = opts.token || null;
    function cancelled() { return !!(token && token.cancelled); }

    lastFocus = document.activeElement;
    /* ONLY a prompt we FORCED spends the one-per-page-view budget. A shopper who opens this
       picker herself has not been stopped by us, and burning the prompt on her own browse would
       disarm the intercept for exactly the shoppers most engaged with the offer. */
    if (via === 'checkout_intercept' || via === 'checkout_upsell') spendPrompt(opts.mode);

    /* Upsell mode swaps the picker grid for a single navigating CTA. Set BEFORE render(), which
       branches on this class. */
    var upsell = opts.mode === 'upsell';
    modal.classList.toggle('is-upsell', upsell);
    var grid = $('[data-ofm-grid]'), ups = $('[data-ofm-upsell]');
    if (grid) grid.hidden = upsell;
    if (ups) ups.hidden = !upsell;
    /* ONE primary action in upsell mode. The footer's "Checkout →" is a second full-width black
       button that competes with the shop CTA for the same eye and routes exactly where the skip
       below it already routes — three stacked actions, two of them identical. It stands down
       here; the skip does its job in the shape of a secondary, which is also what makes it
       obvious that continuing is optional. */
    var go = $('.ofm__go'), skip = $('[data-ofm-skip]');
    if (go) go.hidden = upsell;
    if (skip) skip.textContent = upsell ? M.UPSELL_COPY.skip : 'No thanks, checkout now';

    if (overlay) { overlay.hidden = false; requestAnimationFrame(function () { if (cancelled()) return; overlay.classList.add('is-open'); }); }
    modal.hidden = false;
    requestAnimationFrame(function () {
      if (cancelled()) return;
      modal.classList.add('is-open');
      /* The ONLY signal that the modal is genuinely serviceable. Read synchronously right after
         open() returns and it is always absent — this class is set here, a frame later — which is
         how an earlier draft of this fix would have sent every shopper past the modal. */
      if (opts.onOpened) opts.onOpened();
    });
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('hl-noscroll');
    /* Render immediately from what we already know (cfg.qty is exact at page load, and any
       mutation since then arrived via hl:cart:updated), then top up the thumbnails and subtotal
       once /cart.js lands. Fetching here rather than at load keeps a request off every page
       view — the copy and the pips never wait on it, only the decorative snapshot does. */
    render();
    if (!cart) getCart().then(function (c) { if (c) { cart = c; render(); } }).catch(function () { });
    var c = $('[data-ofm-close]'); if (c) setTimeout(function () { c.focus(); }, 40);
    if (window.hlTrack) {
      try {
        var ost = stateNow();
        window.hlTrack('hl_offer_modal_opened', {
          elig_qty: ost && ost.units, pick_qty: ost && ost.picks,
          alloc_state: ost && ost.allocState, rollout: ost && ost.rollout,
          need_paid: ost && ost.paidLeft, picks_left: ost && ost.picksLeft,
          /* `via` separates the two entry points: 'drawer_cta' is the shopper choosing to look,
             'checkout_intercept' is us stopping them. Same modal, completely different intent,
             and only this field tells the conversion rates apart. */
          short_by: ost && ost.slotsNeed, via: via || 'unknown'
        });
      } catch (err) { /* never block the modal on a metric */ }
    }
  }

  /* renderOffer() replaces [data-cd-offer-ui]'s innerHTML on every cart change, and the drawer
     CTA lives inside it — so a modal opened from that CTA has a DETACHED lastFocus by the time
     it closes if the shopper added anything. .focus() on a detached node is a silent no-op that
     drops focus to <body>, leaving a keyboard or screen-reader shopper at the very top of the
     document. Re-resolve against the live DOM, and fall back to controls that are never
     re-rendered rather than leaving focus nowhere. */
  function restoreFocus() {
    var target = null;
    if (lastFocus && document.contains(lastFocus) && lastFocus.focus) {
      target = lastFocus;
    } else {
      target = document.querySelector('[data-hl-pick-free]')
            || document.querySelector('#cartDrawer [data-hl-checkout]')
            || document.querySelector('#cartDrawer .cd__close');
    }
    if (target && target.focus) {
      try { target.focus(); } catch (err) { /* focus is never worth throwing over */ }
    }
  }

  /* `reason` is the whole point of instrumenting this: "dismissed" is not one behaviour.
     Closing to go edit the cart, hitting Escape, and clicking the skip link straight to
     checkout are three different shoppers. Defaults to 'unknown' so a future call site that
     forgets to pass one shows up as a gap rather than silently miscounting an existing bucket. */
  function close(reason) {
    modal.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('hl-noscroll');
    /* Reset upsell mode only AFTER the fade, or the picker grid pops back into view mid-transition
       on the way out. */
    setTimeout(function () {
      modal.hidden = true; if (overlay) overlay.hidden = true;
      modal.classList.remove('is-upsell');
      var g = $('[data-ofm-grid]'), u = $('[data-ofm-upsell]'), go2 = $('.ofm__go');
      if (g) g.hidden = false;
      if (u) u.hidden = true;
      if (go2) go2.hidden = false;   // restore the footer's checkout button for picker mode
    }, 220);
    restoreFocus();
    if (window.hlTrack) {
      window.hlTrack('hl_offer_dismissed', {
        reason: typeof reason === 'string' ? reason : 'unknown'
      });
    }
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }

  function add(variantId, btn, via) {
    if (!variantId) return;
    /* Keep the original markup: option rows are <span>title</span><i>price</i>, so a plain
       textContent restore on failure would flatten them into the word "Add". */
    var was = btn.innerHTML;
    btn.disabled = true; btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    })
      .then(function (r) { if (!r.ok) return r.json().then(function (x) { throw x; }); return r.json(); })
      /* Route the refresh through hl-home.js so the drawer behind the modal updates from the
         same cart we do, and we both hear one hl:cart:updated. Falls back to a local fetch if
         that API isn't there (e.g. a template that loads the modal but not hl-home.js). */
      .then(function () {
        /* Emitted BEFORE the refresh, not after: refresh() re-renders and can replace this
           button's DOM, and an await that never resolves would lose the event for an add that
           actually succeeded. */
        if (window.hlTrack) {
          window.hlTrack('hl_offer_add_success', { variant_id: String(variantId), via: via || 'unknown' });
        }
        if (window.hlOffer && typeof window.hlOffer.refresh === 'function') return window.hlOffer.refresh(false);
        return getCart().then(function (c) { if (c) { cart = c; render(); } });
      })
      .catch(function () {
        btn.disabled = false; btn.innerHTML = was;
        var s = $('[data-ofm-sub]'); if (s) s.textContent = 'Could not add that one — try another.';
        if (window.hlTrack) {
          window.hlTrack('hl_offer_add_failed', { variant_id: String(variantId), via: via || 'unknown' });
        }
      });
  }

  modal.addEventListener('click', function (e) {
    if (e.target.closest('[data-ofm-close]')) { e.preventDefault(); close('close_button'); return; }
    if (e.target.closest('[data-ofm-back]')) { closeSheet(); return; }
    var skip = e.target.closest('[data-ofm-skip]');
    if (skip && window.hlTrack) {
      /* Not a dismissal — it is the shopper choosing checkout over the offer, and it must stay
         a real <a href="/checkout"> the intercept skips. Emit and get out of the way. */
      window.hlTrack('hl_offer_skip_to_checkout', {});
    }
    var addBtn = e.target.closest('[data-ofm-add]');
    if (addBtn) {
      var vid = addBtn.getAttribute('data-variant-id');
      if (vid) { add(vid, addBtn, 'card'); return; }
      openSheet(addBtn.closest('[data-ofm-card]'));
      return;
    }
    /* Must come BEFORE the .ofm__opt branch — the reveal is not an add. */
    var more = e.target.closest('[data-ofm-more]');
    if (more) {
      var list = $('[data-ofm-optlist]');
      if (list) list.hidden = false;
      more.hidden = true;
      var f = list && list.querySelector('.ofm__opt:not([disabled])');
      if (f) f.focus();
      return;
    }
    var opt = e.target.closest('.ofm__opt');
    if (opt && !opt.disabled) {
      /* 'samesize' vs 'list' is the numerator for the exposure ratio emitted in openSheet. */
      add(opt.getAttribute('data-vid'), opt,
          opt.classList.contains('ofm__opt--same') ? 'samesize' : 'list');
    }
  });

  /* Wrapped, NOT `addEventListener('click', close)` — that hands the click Event straight to
     close() as its `reason`, which would land every overlay dismissal in the 'unknown' bucket. */
  if (overlay) overlay.addEventListener('click', function () { close('overlay'); });
  /* Escape backs out one level at a time: sheet first, then the modal. Closing the whole modal
     from inside the option sheet would throw away the shopper's place in the grid. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !modal.classList.contains('is-open')) return;
    var sheet = $('[data-ofm-sheet]');
    if (sheet && !sheet.hidden) { closeSheet(); return; }
    close('escape');
  });

  /* ---- §2.7 stale-tab guard -------------------------------------------------------------
     ONE shot per page view, ~1200 ms, NO retry. Every failure mode — timeout, non-200, missing
     blob, bad JSON, a throw — resolves to "do not intercept", because the contract is that
     nothing in this file may ever be why a shopper cannot check out. A missed nudge costs a
     nudge; a stranded checkout costs an order. */
  /* NEVER caches a completed check. This used to short-circuit on `if (revalidated) return
     cb(true)`, which meant any second prompt in a page view skipped the server entirely — exactly
     the stale-tab-after-a-rollback case this function exists to catch. Keying that cache by
     mode + cart token would not have fixed it either: the rollout flag can change server-side
     without the cart or the mode moving at all. So only IN-FLIGHT requests are coalesced (which
     is all a double-click needs), and every checkout intent that can swallow a click is backed by
     its own live answer.

     Calls back with the FRESH derived state, or null on any failure — bad JSON, a non-200, a
     timeout, a throw. Null always means "do not stop her". */
  var inflight = null;
  function revalidate(cb) {
    if (inflight) { inflight.push(cb); return; }
    inflight = [cb];
    var settled = false;
    function finish(v) {
      if (settled) return;
      settled = true;
      var waiting = inflight; inflight = null;
      for (var i = 0; i < waiting.length; i++) {
        try { waiting[i](v); } catch (err) { /* one bad callback must not strand the others */ }
      }
    }
    var timer = setTimeout(function () { finish(null); }, 1200);
    try {
      fetch('/cart?view=offercfg', { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          clearTimeout(timer);
          if (!html) return finish(null);
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var el = doc.querySelector('[data-cd-offer2]');
          if (!el) return finish(null);
          var fresh = JSON.parse(el.textContent);
          if (!fresh || fresh.v !== 2) return finish(null);
          /* Adopt it for the rest of the page view — the drawer reads the same object. */
          cfg = fresh;
          if (window.hlOffer) window.hlOffer.cfg = fresh;
          finish(stateNow());
        })
        .catch(function () { clearTimeout(timer); finish(null); });
    } catch (err) { clearTimeout(timer); finish(null); }
  }

  /* Send her where the click was going. The drawer's control is <a href="/checkout">; the /cart
     page's is <button type="submit" name="checkout">, whose NAME is what tells Shopify to check
     out rather than update — so it has to be re-added by hand, since form.submit() does not
     include the activating button. */
  function proceed(el) {
    try {
      if (el.tagName === 'A' && el.href) { window.location.href = el.href; return; }
      var form = el.form || (el.closest && el.closest('form'));
      if (form) {
        if (el.name) {
          var h = document.createElement('input');
          h.type = 'hidden'; h.name = el.name; h.value = el.value || '1';
          form.appendChild(h);
        }
        form.submit();
        return;
      }
    } catch (err) { /* fall through */ }
    window.location.href = '/checkout';
  }

  /* THE CHECKOUT GATE. Capture phase + stopImmediatePropagation so it runs before, and instead
     of, any other handler bound to the same checkout control.

     TWO prompts live here now, and the model decides which (or neither) via `checkoutMode`:
       'intercept' — she has earned NOTHING and we can see that clearly. The original prompt.
       'upsell'    — she HAS a rung and another is still reachable (2026-08-19). This is the case
                     the operator's 2026-08-18 "no modal if they have the offer" rule used to
                     exclude; he amended it for exactly this window.
     Everything else — mismatch, an unrelated discount winning, an unreadable cart, the flag off,
     an empty cart, the top rung already held — is 'none' and the click proceeds untouched.

     The budget is one prompt per page view (see mayPrompt): a shopper who has been stopped once
     and clicks checkout again goes straight through. A prompt that must be dismissed twice reads
     as a trap. */
  /* Every DOM node render() will write into. If ANY is missing or detached we must not swallow
     the click at all — a bare truthiness check is not enough, because `modal` is captured once at
     load and stays truthy after the node is removed from the document, which would let open()
     "succeed" off-DOM with checkout already cancelled. */
  var REQUIRED = ['[data-ofm-title]', '[data-ofm-sub]', '[data-ofm-prog]', '[data-ofm-thumbs]',
                  '[data-ofm-count]', '[data-ofm-subtotal]'];
  function nodesReady() {
    if (!modal || !modal.isConnected) return false;
    for (var i = 0; i < REQUIRED.length; i++) {
      var el = $(REQUIRED[i]);
      if (!el || !el.isConnected) return false;
    }
    return true;
  }

  /* The live attempt that has swallowed a click, or null. At most one exists at a time. */
  var attempt = null;
  var OPEN_TIMEOUT_MS = 600;      // how long a queued animation frame may take before we give up
  var ATTEMPT_TIMEOUT_MS = 2200;  // backstop covering revalidate's own 1200ms plus the open window

  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-hl-checkout]');
    if (!link || modal.contains(link)) return;
    /* A second rapid click COALESCES into the live attempt and takes its outcome. It must not be
       allowed through natively: attempt #1 still holds a timer that could later proceed() a second
       time, and a queued frame that could open the modal over a navigation already underway. */
    if (attempt) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    var st = stateNow();
    /* checkoutMode comes from the model and is derived from what the SERVER actually granted, so
       it cannot drift from what checkout will charge. The four gates below are ordered cheapest
       first, and every one of them EXITS WITHOUT touching the event — the click is only ever
       swallowed once we know we can service it. */
    if (!st) return;
    var mode = st.checkoutMode;              // 'none' | 'intercept' | 'upsell'
    if (mode === 'none') return;             // mismatch / other / unknown / flag off / empty cart
    if (!mayPrompt(mode)) return;            // budget already spent this page view
    if (!nodesReady()) return;               // never preventDefault a click we cannot service

    /* We WOULD stop her — so confirm against fresh server state first. Everything above came from
       a page-load photograph, and a tab opened before a rollback still believes the feature is on.

       From here until finish() runs, WE own her click. Success, failure, throw, timeout and every
       non-open branch route through one idempotent finish(), so she is navigated exactly once —
       never zero times (stranded) and never twice. */
    e.preventDefault();
    e.stopImmediatePropagation();

    var a = attempt = { cancelled: false, done: false, timer: null };
    function finish(opened) {
      if (a.done) return;
      a.done = true;
      if (a.timer) { clearTimeout(a.timer); a.timer = null; }
      if (attempt === a) attempt = null;
      if (!opened) {
        /* Cancel before navigating: a frame queued by open() may still be pending, and it must
           not paint a modal over a checkout already in flight. */
        a.cancelled = true;
        proceed(link);
      }
    }
    /* Backstop. If revalidate never calls back at all — a hung promise, a browser quirk — this is
       what keeps the click from being lost. */
    a.timer = setTimeout(function () { finish(false); }, ATTEMPT_TIMEOUT_MS);

    revalidate(function (fresh) {
      try {
        /* Re-check the SAME mode we swallowed the click for. A stale tab whose flag flipped to
           `off`, or a cart that changed underneath us, proceeds untouched. */
        if (!fresh || fresh.checkoutMode !== mode || !mayPrompt(mode) || !nodesReady()) {
          return finish(false);
        }
        var opened = false;
        var openTimer = setTimeout(function () { if (!opened) finish(false); }, OPEN_TIMEOUT_MS);
        open(mode === 'intercept' ? 'checkout_intercept' : 'checkout_upsell', {
          mode: mode,
          token: a,
          onOpened: function () {
            opened = true;
            clearTimeout(openTimer);
            /* Only a modal that is genuinely in the document and visibly open counts as having
               serviced the click. Anything else falls through to checkout. */
            finish(modal.isConnected && modal.classList.contains('is-open'));
          }
        });
      } catch (err) {
        finish(false);
      }
    });
  }, true);

  /* hl-home.js broadcasts this on every cart change, so `cart` is current from the first
     mutation onward without this file ever polling. */
  document.addEventListener('hl:cart:updated', function (e) {
    if (e.detail) { cart = e.detail; if (modal.classList.contains('is-open')) render(); }
  });

  /* No public open() API on purpose. One briefly existed so the drawer's free-picks CTA could
     open this picker in place; the operator ruled 2026-08-18 that the CTA navigates to the
     picks collection instead, which left the export with no callers. The checkout intercept
     below is now the only way in. If a second entry point is ever added, publish the API here
     (LAST, after every guard above) rather than reaching into this closure. */
})();
